import React, { Suspense, lazy, useState } from "react";
import ErrorBoundary from "./ErrorBoundary";
import LoadingState from "./LoadingState";
import StatusBanner from "./StatusBanner";
import useDataset from "./hooks/useDataset";
import "./App.css";

const TopExtensionsChart = lazy(
  () => import("./components/charts/TopExtensionsChart"),
);
const CategoryMarketShareChart = lazy(
  () => import("./components/charts/CategoryMarketShareChart"),
);
const AverageRatingChart = lazy(
  () => import("./components/charts/AverageRatingChart"),
);
const UsersVsRatingChart = lazy(
  () => import("./components/charts/UsersVsRatingChart"),
);
const TopSubcategoriesChart = lazy(
  () => import("./components/charts/TopSubcategoriesChart"),
);
const RankDistributionChart = lazy(
  () => import("./components/charts/RankDistributionChart"),
);
const CategoryRatingDistributionChart = lazy(
  () => import("./components/charts/CategoryRatingDistributionChart"),
);
const TotalUsersByCategoryChart = lazy(
  () => import("./components/charts/TotalUsersByCategoryChart"),
);
const TopExtensionsMarketShareChart = lazy(
  () => import("./components/charts/TopExtensionsMarketShareChart"),
);
const UsersDistributionChart = lazy(
  () => import("./components/charts/UsersDistributionChart"),
);
const ParetoUsersChart = lazy(
  () => import("./components/charts/ParetoUsersChart"),
);

// Additional chart imports
const AverageUsersByCategoryChart = lazy(
  () => import("./components/charts/AverageUsersByCategoryChart"),
);
const CategorySubcategoryBreakdownChart = lazy(
  () => import("./components/charts/CategorySubcategoryBreakdownChart"),
);
const ExtensionsCountByCategoryChart = lazy(
  () => import("./components/charts/ExtensionsCountByCategoryChart"),
);
const GrowthTrendChart = lazy(
  () => import("./components/charts/GrowthTrendChart"),
);
const RatingCountVsUsersChart = lazy(
  () => import("./components/charts/RatingCountVsUsersChart"),
);
const SubcategoryMarketShareChart = lazy(
  () => import("./components/charts/SubcategoryMarketShareChart"),
);
const UnderratedExtensionsChart = lazy(
  () => import("./components/charts/UnderratedExtensionsChart"),
);
const FeaturedVsNonFeaturedChart = lazy(
  () => import("./components/charts/FeaturedVsNonFeaturedChart"),
);

// Additional component imports
const AppHeader = lazy(() => import("./AppHeader"));
const CategoryFilter = lazy(() => import("./CategoryFilter"));
const KpiOverview = lazy(() => import("./KpiOverview"));
const ExtensionDashboard = lazy(
  () => import("./components/ExtensionDashboard"),
);

function App() {
  const { loading, error, data } = useDataset();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showSubcategories, setShowSubcategories] = useState(true);
  const [showTop15, setShowTop15] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-700">
      {/* ===== NAVBAR ===== */}
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-center">
            <div className="flex items-center gap-3">
              <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent tracking-tight">
                Chrome Web Store Analytics
              </span>
              <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold border border-slate-200 shadow-sm">
                2023
              </span>
            </div>
          </div>
        </div>
      </nav>
      {/* ================= HEADER ================= */}

      {/* ===== MAIN ===== */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* ================= INITIAL LOADING / ERROR ================= */}
        {loading && <LoadingState label="Fetching Chrome Web Store data..." />}

        {error && (
          <StatusBanner
            variant="error"
            title="Dataset load failed"
            message="Unable to load extension dataset. Please try again later."
          />
        )}

        {/* ================= CONTENT ================= */}
        {!loading && !error && (
          <ErrorBoundary>
           

            {/* ================= MARKET LEADERS ================= */}
            <section className="mb-12">
              <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-indigo-600 rounded-full"></span>
                Market Leaders & Trends
              </h2>

              <div className="w-full mb-6">
                <Suspense fallback={<LoadingState label="Loading chart..." />}>
                  <TopExtensionsChart />
                </Suspense>
              </div>

              <div className="w-full mb-6">
                <Suspense fallback={<LoadingState label="Loading chart..." />}>
                  <TopExtensionsMarketShareChart />
                </Suspense>
              </div>

              <div className="w-full">
                <Suspense fallback={<LoadingState label="Loading chart..." />}>
                  <CategoryMarketShareChart />
                </Suspense>
              </div>
            </section>

            {/* ================= CATEGORY ANALYSIS ================= */}
            <section className="mb-12">
              <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-indigo-600 rounded-full"></span>
                Category Analysis
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="chart-card">
                  <Suspense
                    fallback={<LoadingState label="Loading chart..." />}
                  >
                    <TotalUsersByCategoryChart />
                  </Suspense>
                </div>

                <div className="chart-card">
                  <Suspense
                    fallback={<LoadingState label="Loading chart..." />}
                  >
                    <ExtensionsCountByCategoryChart />
                  </Suspense>
                </div>
              </div>

              <div className="chart-card">
                {/* Toggle buttons for subcategories vs market share */}
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {showSubcategories 
                        ? showTop15 
                          ? "Top 15 Subcategories" 
                          : "Top 10 Subcategories"
                        : "Subcategory Market Share"}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {showSubcategories 
                        ? showTop15 
                          ? "Most popular 15 subcategories by extension count"
                          : "Top 10 subcategories by extension volume"
                        : "Market share distribution across subcategories"}
                      }
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowSubcategories(true); setShowTop15(false); }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        showSubcategories && !showTop15
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                      }`}
                    >
                      Top 10
                    </button>
                    
                    <button
                      onClick={() => { setShowSubcategories(true); setShowTop15(true); }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        showSubcategories && showTop15
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                      }`}
                    >
                      Top 15
                    </button>
                  </div>
                </div>

                {/* Toggle content */}
                <Suspense fallback={<LoadingState label="Loading chart..." />}>
                  {showSubcategories ? (
                    <TopSubcategoriesChart showTop15={showTop15} />
                  ) : (
                    <SubcategoryMarketShareChart />
                  )}
                </Suspense>
              </div>
            </section>

            {/* ================= RATINGS & QUALITY ================= */}
            <section className="mb-12">
              <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-indigo-600 rounded-full"></span>
                Ratings & Quality
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="chart-card">
                  <Suspense
                    fallback={<LoadingState label="Loading chart..." />}
                  >
                    <AverageRatingChart />
                  </Suspense>
                </div>

                <div className="chart-card">
                  <Suspense
                    fallback={<LoadingState label="Loading chart..." />}
                  >
                    <CategoryRatingDistributionChart />
                  </Suspense>
                </div>
              </div>

              
              

            </section>

            {/* ================= USER DISTRIBUTION ANALYSIS ================= */}
            <section className="mb-12">
              <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-indigo-600 rounded-full"></span>
                User Distribution Analysis
              </h2>

              <div className="w-full mb-6">
                <Suspense fallback={<LoadingState label="Loading chart..." />}>
                  <UsersDistributionChart />
                </Suspense>
              </div>

              
            </section>

            {/* ================= TRENDS & GROWTH ================= */}
            

            {/* ================= DEEP DIVE ================= */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-indigo-600 rounded-full"></span>
                Deep Dive Analysis
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="chart-card">
                  <Suspense
                    fallback={<LoadingState label="Loading chart..." />}
                  >
                    <UsersVsRatingChart />
                  </Suspense>
                </div>

                <div className="chart-card">
                  <Suspense
                    fallback={<LoadingState label="Loading chart..." />}
                  >
                    <RankDistributionChart />
                  </Suspense>
                </div>
              </div>

              
            </section>
          </ErrorBoundary>
        )}
      </main>

      {/* ===== FOOTER ===== */}
    </div>
  );
}

export default App;
